import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { IUserStatus, QueueStats } from '../../../types';
import { CallDebounce, CallWhenAvailableUser, StatusStatus } from './slice';

export const selectUserStatuses = createSelector(
    [(state: RootState) => state.status.userStatuses],
    (statuses: Record<string, IUserStatus>): Record<string, IUserStatus> => statuses
);

export const selectCallWhenAvailableList = (state: RootState): CallWhenAvailableUser[] => state.status.callWhenAvailable || [];

export const selectCallDebounce = (state: RootState): CallDebounce | undefined =>
    state.status.callDebounce;

export const selectPostCallFetcher = (state: RootState): number | undefined =>
    state.status.postCallFetcher;

export const selectUserStatus = createSelector(
    [selectUserStatuses, (_: RootState, userName: string | undefined) => userName],
    (userStatuses, userName: string | undefined): IUserStatus | undefined =>
        typeof userName === 'string' ? userStatuses[userName.toLowerCase()] : undefined
);

export const selectUserAuxCode = createSelector(
    [selectUserStatus],
    (userStatus: IUserStatus | undefined): string | undefined => userStatus?.aux_code?.code
);

export const selectUserGlobalDnd = createSelector(
    [selectUserStatus],
    (userStatus: IUserStatus | undefined): boolean => userStatus?.global_dnd || false
);

export const selectQueueAvailability = createSelector(
    [selectUserStatus],
    (userStatus: IUserStatus | undefined): boolean => userStatus?.queue_available || false
);

export const selectQueueLogin = createSelector(
    [selectUserStatus],
    (userStatus: IUserStatus | undefined): boolean => userStatus?.queue_login || false
);

export const selectLoginUsers = createSelector(
    [
        (state: RootState): Record<string, QueueStats> => state.queue.queueStats || {},
        (_: RootState, queueUuid: string) => queueUuid,
    ],
    (queueStats, queueUuid: string): string[] => {
        const stats = queueStats[queueUuid];

        if (!stats) {
            return [];
        }

        return stats.login_users;
    }
);

export const selectStatusApi = (state: RootState): StatusStatus => state.status.userStatusAPI;

export const selectNewFetchUsers = createSelector(
    [(state: RootState): string[] => state.status.newFetchUsers],
    (fetchUsers?: string[]) => fetchUsers || []
);

export const selectIsUserNewFetch = createSelector(
    [selectNewFetchUsers, (_: any, user: string) => user],
    (fetchUsers: string[], user: string): boolean => fetchUsers.includes(user)
);

export const selectSpecificQueueLoginUsers = createSelector(
    [
        (state: RootState): Record<string, QueueStats> => state.queue.queueStats || {},
        (_: RootState, queueUuid: string | undefined) => queueUuid,
    ],
    (queueStats, queueUuid: string | undefined): string[] | undefined => {
        if (!queueUuid) {
            return undefined;
        }

        const stats = queueStats[queueUuid];

        if (!stats) {
            return undefined;
        }

        return stats.login_users;
    }
);

export const selectUserSpecificQueueLogin = createSelector(
    [
        selectUserStatuses,
        selectSpecificQueueLoginUsers,
        (_: RootState, queueUuid: string | undefined) => queueUuid,
        (_: RootState, __: any, userName: string | undefined) => userName,
        (_: RootState, __: any, ___: any, userUuid: string) => userUuid,
    ],
    (
        userStatuses,
        login_users,
        queueUuid: string | undefined,
        userName: string | undefined,
        userUuid
    ): { allQueues: boolean, specificQueue: boolean } | undefined => {
        if (!userName || !queueUuid) {
            return undefined;
        }

        const userStatus = typeof userName === 'string' ? userStatuses[userName.toLowerCase()] : undefined;
        const allQueues = userStatus?.queue_login || false;

        const specificQueue = login_users?.some(u => u === userUuid) || false;

        return { allQueues, specificQueue };

    }
);
